/**
 * Traducciones para i18n - Inglés y Español
 * Usar claves con punto: t('nav.home'), t('home.heroTitle')
 * Para interpolación: t('contact.thanks', { name: 'Juan' }) → "Gracias Juan! ..."
 */

export const translations = {
  en: {
    // ===== NAV =====
    nav: {
      home: 'Home',
      aboutUs: 'About Us',
      contact: 'Contact',
      login: 'Login',
      register: 'Register',
      dashboard: 'Dashboard',
      profile: 'Profile',
      signOut: 'Sign Out',
      toggleMenu: 'Toggle menu',
      closeMenu: 'Close menu',
      userMenu: 'User menu',
      langEn: 'EN',
      langEs: 'ES',
      translateToSpanish: 'Translate to Spanish',
      translateToEnglish: 'Translate to English',
    },

    // ===== DIALOG =====
    dialog: {
      information: 'Information',
      confirm: 'Confirm',
      cancel: 'Cancel',
      ok: 'OK',
    },

    // ===== HOME =====
    home: {
      heroTitle: "WEDDING PLANNING MADE HUMAN.",
      heroDescription: "If you're juggling spreadsheets, WhatsApp groups and budget chaos — this is your control center.",
      heroSlogan: "(all our tools are for FREE while we launch)",
      sectionTitle: 'Finally, a Wedding Tool That Gets Real',
      sectionSubtitle: 'Built with real wedding pros, so you can skip the chaos and keep the joy',
      paragraph1: "Emails, coffee runs, Netflix binges? Your wedding shouldn't take over everything.",
      paragraph2: "We're building tools for straightforward (and sanity-saving) wedding planning. No endless runsheets, no binder explosions — just clarity and flow.",
      paragraph3: "Sign up today and get free access to every tool as it goes live — until further notice. No tiers, no pressure. Just early access.",
      paragraph4: "We get it — weddings are stressful (for you, your partner, and let's be real… even auntie Karen). That's why real wedding professionals are behind the scenes, making sure nothing slips through the cracks.",
      paragraph5: "The Unfiltered Collective.",
      paragraph5b: "Leave the fire-fighting to us — you've got better things to do (like actually enjoying being engaged).",
      ctaButton: 'Take Back Control',
    },

    // ===== ABOUT US =====
    about: {
      title: "WHY YOU'LL LOVE HAVING",
      titleLine2: "US ON YOUR TEAM",
      feature01Title: 'A Checklist Built From Real Wedding Days',
      feature01Text: "Behind the scenes of this platform is a team who's lived through hundreds of wedding days. We've seen what works, what couples forget, and the little things that save the day. That experience is baked right into your planning hub — so every checklist, reminder, and tip feels less like a random template and more like having a calm, seasoned coordinator at your side.",
      feature02Title: 'Stay on Top of Your Budget',
      feature02Text: "Money talks, and weddings make it shout. With clear overviews and gentle nudges, you'll always know what's been covered and what's coming up next. Think of it as having someone in your corner keeping the numbers neat, so you can focus on the fun.",
      feature03Title: 'Guest List, Tamed',
      feature03Text: "RSVPs, plus-ones, dietary quirks — they all have a home here. Instead of scattered notes and family group chats, you'll see the full picture unfold in one tidy place. It's like having a personal assistant who quietly keeps track so you don't have to.",
      feature04Title: 'Vendors at Your Fingertips',
      feature04Text: "From contracts to phone numbers, everything about your suppliers lives together where you can actually find it. No digging through old emails when you need to confirm delivery times — just the calm reassurance of knowing it's all right there.",
      feature05Title: 'Keep It All in One Place',
      feature05Text: "As you move through your planning, see everything come together in one hub, all aligned and organised. It's the kind of clarity that makes you exhale and realise, yes, this is under control.",
      feature06Title: 'Celebrate Without the Chaos',
      feature06Text: "By the time your big day rolls around, you'll have:",
      feature06Li1: 'All your wedding info in one organised, calm, easy-to-use hub.',
      feature06Li2: 'No frantic searching through group chats.',
      feature06Li3: 'More time for champagne, less time for spreadsheets.',
      promiseTitle: 'THE UNFILTERED PROMISE:',
      promiseText: 'To hold space for the beautiful in-between.',
    },

    // ===== CONTACT =====
    contact: {
      title: 'CONTACT US',
      description: "Have a question or want to get in touch? Fill out the form below and we'll get back to you as soon as possible.",
      firstName: 'First Name',
      lastName: 'Last Name',
      phoneNumber: 'Phone Number',
      email: 'Email address',
      inquiryLabel: 'What would you like to inquire about?',
      placeholderName: 'First Name',
      placeholderLastname: 'Last Name',
      placeholderPhone: '+1 (555) 123-4567',
      placeholderEmail: 'your@email.com',
      placeholderMessage: 'Tell us about your inquiry...',
      sendMessage: 'Send Message',
      sending: 'Sending...',
      successMessage: "Thank you {{name}}! We've received your message. We'll get back to you as soon as possible.",
      errorSubmit: "There was an error submitting the form. Please try again.",
      errorNetwork: 'Network error. Please try again in a moment.',
    },

    // ===== AUTH =====
    auth: {
      loginWelcome: 'Welcome back! Sign in to continue planning your perfect wedding.',
      registerWelcome: 'Welcome to your calm corner of wedding planning. Take a breath, fill in your details, and start building your wedding dashboard — finally, a space that makes planning feel doable.',
      emailLabel: 'Email address',
      passwordLabel: 'Password',
      forgotPassword: 'Forgot your password?',
      showPassword: 'Show',
      hidePassword: 'Hide',
      signIn: "Let's go",
      signingIn: 'Signing in...',
      signUp: 'Sign Up',
      signInLink: 'Sign In',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      nameLabel: 'Name',
      lastNameLabel: 'Last Name',
      partnerName: 'Partner Name',
      partnerLastName: 'Partner Last Name',
      partnerEmail: "Partner's email address",
      weddingDate: 'Wedding Date',
      budget: 'Budget',
      letsDoThis: "Let's do this",
      registering: 'Registering...',
      errorSignIn: 'Error signing in. Please try again.',
      errorRegister: 'Error registering. Please try again.',
      fillRequired: 'Please fill in all required fields',
      passwordMin: 'Password must be at least 6 characters long',
      emailExists: 'This email is already registered. Please sign in or use a different email.',
      partnerEmailMustBeDifferent: 'Partner email must be different from your email. Please use another email.',
      validEmail: 'Please enter a valid email address.',
      accountCreated: 'Your account has been created. Please log in with your email and password to start using your dashboard.',
      checkEmail: 'Please check your email to confirm your account, then log in.',
    },

    // ===== FOOTER =====
    footer: {
      instagramText: 'Head to our IG for practical wedding tips and the stuff no one tells you about planning a wedding.',
      followInstagram: 'Follow us on Instagram',
      copyright: '© 2025 The UNFILTERED COLLECTIVE. All rights reserved.',
      credit: 'Designed and developed by',
    },

    // ===== SIDEBAR / DASHBOARD =====
    sidebar: {
      toggle: 'Toggle sidebar',
      close: 'Close menu',
      dashboard: 'Dashboard',
      checklist: 'Checklist',
      budgetManager: 'Budget Manager',
      timelineCreator: 'Timeline Creator',
      guestManager: 'GUEST MANAGER',
      guestList: 'Guest List',
      lifesaverLink: 'The Lifesaver Link',
      craftInvite: 'Craft Your Invite',
      gentleReminder: 'Gentle Reminder Time',
      whoSitsWhere: 'Who Sits Where',
      supplierManager: 'SUPPLIER MANAGER',
      numbersPlease: 'Numbers please',
      chosenOnes: 'The chosen ones',
      lists: 'LISTS',
      mustCapture: 'Must-Capture Moments',
      soundtrack: 'The Soundtrack',
      bloomBoard: 'Bloom Board',
      weddingCargo: 'Wedding Cargo',
    },

    // ===== PRICING =====
    pricing: {
      title: 'PRICING',
      findFit: 'Find your fit',
      freeNow: 'For now, all tools are free.',
      freeText1: 'Start planning your wedding today with our complete suite of free tools.',
      freeText2: 'No credit card required, no hidden fees. Just sign up and start planning.',
      smoothText: 'Wedding planning made smooth (without the scary price tag).',
      description1: "Here's what you get with each plan—clear, simple, and built to keep your wedding planning moving without the chaos.",
      description2: 'All tools are currently free and fully functional.',
      description3: 'Every package comes with the essentials. The difference? How much support and structure you want along the way.',
      chillMode: 'CHILL MODE',
      chillDesc: "(It's free... and all tools are live now.)",
      levelUp: 'LEVEL UP',
      levelUpDesc: 'More control, less chaos.',
      weddingCeo: 'WEDDING CEO',
      ceoDesc: 'Full control. Full calm.',
      backToPricing: 'Back to Pricing',
      live: 'LIVE',
      // Chill Mode features
      checklistTitle: 'Dynamic Wedding Checklist',
      checklistDesc: 'A pre-assigned, fully editable checklist that automatically adjusts to your wedding date.',
      checklistTag1: 'Calculating, no panic math.',
      checklistTag2: 'Peace of mind, built-in.',
      rsvpTitle: "RSVP's",
      rsvpDesc: "Tracking who's in (and who's out) has never been easier. Guests RSVP Yes/No, add their plus-one, and you can customise your invite with any photo. Keep everything in one place and smoothly assign tables in the Guest Manager.",
      rsvpTag: 'No messy spreadsheets. No endless group chats. Just RSVPs that actually make sense.',
      guestManagerTitle: 'Guest Manager',
      guestManagerDesc: 'Keep every guest in one simple list — with tags like family, friends, work, so you instantly know who\'s who. A mini dashboard shows X confirmed / Y total invited.',
      guestManagerTag: 'Clear, organised, and way easier than juggling five lists — plus, you can manually allocate guests to tables.',
      budgetTrackerTitle: 'Budget Tracker',
      budgetTrackerDesc: 'Set your wedding budget when you sign up. Then, just add expenses as you go — the tracker shows you exactly what % of your budget you\'ve used.',
      budgetTrackerTag: 'No financial panic. Just clarity.',
      // Level Up features
      rsvpLevelDesc: 'Get even more control. Customise your RSVP form to collect dietary requirements, tag guests by group — family, friends, or "needs accommodation" — and send tailored questions to each list.',
      rsvpLevelDesc2: 'With the lifesaver link, guest details are collected faster, smoother, and with way less stress. Smart RSVP management, done right.',
      suppliersTitle: 'Suppliers Manager',
      suppliersDesc: 'Keep all your wedding pros in one place — photographer, florist, catering, you name it. Add their contact details, rates, and key dates so you never lose track (or that one random email thread).',
      suppliersTag: 'One dashboard, zero supplier chaos.',
      timelineTitle: 'Editable Timeline',
      timelineDesc: 'A ready-to-go wedding day schedule you can customise to fit your plans. Adjust the timings, add your own notes, and keep everything in one place — so everyone knows what\'s happening, when.',
      timelineTag: 'No guessing, no chaos. Just a timeline that actually works.',
      customListsTitle: 'Custom Lists',
      customListsDesc: 'Need a shot list for your photographer? A checklist for flowers? A packing list for the big day? Create and edit your own lists so nothing gets forgotten (and no one says "we missed the confetti photo").',
      customListsTag: 'All in one place, stress-free.',
      budgetGraphicsTitle: 'Budget Graphics',
      budgetGraphicsDesc: 'Keep your wedding budget from spiraling out of control. See your spending in clear, colorful charts — track costs, spot overspending, and make sure your dream day doesn\'t bankrupt you.',
      pdfTitle: 'PDF Documents',
      pdfDesc: 'Turn all your wedding plans — timelines, budgets, checklists — into sleek, shareable PDFs. Keep everything in one place, stay on top of every detail, and finally feel like you actually have it together. Stress-free, pretty, and totally under control.',
      // Wedding CEO features
      checklistCeoDesc: 'Your checklist gets even smarter. Tasks come with reminders you can sync directly to your Google Calendar — so nothing slips through the cracks, and you stay two steps ahead.',
      rsvpCeoTitle: "RSVP's & Guest Manager",
      rsvpCeoDesc: 'Take it to the next level. Now you can manage responses across multiple events — rehearsal dinner, ceremony, after-party — all from the same dashboard. Plus, create and customize your seating chart effortlessly.',
      invitesTitle: 'Invitation Designs',
      invitesDesc: "Your invites, your rules. Pick a design, then tweak colors, fonts, and text until it's 100% you. No more boring templates — make them as extra (or chill) as you want.",
      masterSpreadsheet: 'Master Spreadsheet',
      masterSpreadsheetDesc: 'All your wedding chaos in one place. Combine the timeline with vendor schedules, assign tasks per provider, track music for every moment, and know exactly who\'s in charge of what.',
      masterSpreadsheetTag: 'Finally, a plan that actually makes sense.',
      everythingTalks: 'Everything Talks',
      everythingTalksDesc: 'Nothing lives in isolation. Vendors sync with your budget, meeting dates link to tasks, and all your info feeds into the Master Spreadsheet. It\'s like your wedding planning finally grew a brain — and it actually helps you.',
    },

    // ===== PROFILE =====
    profile: {
      freePlan: 'Free Plan',
      totalBudget: 'Total Budget:',
      editInfo: 'Edit Information',
      fullName: 'Full Name',
      email: 'Email',
      phoneOptional: 'Phone (Optional)',
      phonePlaceholder: '+1 234 567 890',
      totalWeddingBudget: 'Total Wedding Budget',
      budgetPlaceholder: '500000',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      cancel: 'Cancel',
      clickToChange: 'Click to change',
      dangerZone: 'Danger Zone',
      dangerWarning: "Once you delete your account, you won't be able to recover it. The following will be permanently deleted:",
      dangerLi1: 'Your authentication account (email, password, authorization to sign in)',
      dangerLi2: 'Your profile (name, personal information, avatar)',
      dangerLi3: 'All your weddings and associated information',
      dangerLi4: 'All your guests and related data',
      dangerFinal: 'This action is permanent and irreversible.',
      deleteAccount: 'Delete My Account',
      confirmTitle: 'Are you sure?',
      confirmText: 'This action cannot be undone. Your account and all your data will be permanently deleted.',
      yesDelete: 'Yes, delete account',
      deleting: 'Deleting...',
      profileUpdated: 'Profile updated successfully',
      errorUpdate: 'Error updating profile. Please try again.',
      invalidImage: 'Please select a valid image file.',
      imageTooLarge: 'The image is too large. Please select an image smaller than 10MB.',
      errorUpload: 'Error uploading profile picture. Please try again.',
      deleteSuccess: 'Your account has been successfully deleted.\n\nWe\'re sorry to see you go! All your wedding planning data has been removed from our system.',
      deleteSuccessPartner: 'Your profile has been successfully deleted.\n\nYour partner will continue to have access to your shared wedding planning information, including guests, tasks, and all other details.',
      deletePartial: 'Your account has been removed from our wedding planning system.\n\nWe\'re sorry to see you go! If you experience any issues, please contact support.',
      deletePartialPartner: 'Your profile has been removed from the wedding planning system.\n\nYour partner will continue to have access to your shared wedding information.\n\nIf you experience any issues, please contact support.',
      errorDelete: 'We encountered an issue while deleting your account.\n\nPlease try again later or contact our support team for assistance.',
    },

    // ===== DASHBOARD HOME =====
    dashboardHome: {
      eventDashboard: 'EVENT DASHBOARD',
      infoContent: "Your wedding is coming together. Here's a quick overview of your planning progress.",
      infoWhat: "What you'll find here:",
      infoCountdown: 'See exactly how much time you have until your big day',
      infoChecklist: 'Track completed and pending tasks',
      infoBudget: 'Monitor your spending and remaining budget',
      infoRsvp: 'See how many guests have confirmed',
      infoNavigate: 'Use the sidebar to navigate to different sections and manage every aspect of your wedding planning.',
      countdown: 'Countdown',
      days: 'Days',
      hours: 'Hours',
      min: 'Min',
      sec: 'Sec',
      checklist: 'Checklist',
      completedTasks: 'Completed tasks:',
      pendingTasks: 'Pending tasks:',
      tasksThisMonth: 'Tasks this month:',
      viewChecklist: 'View checklist →',
      budget: 'Budget',
      spent: 'Spent:',
      remaining: 'Remaining:',
      viewBudget: 'View budget →',
      rsvp: 'RSVP',
      confirmed: 'Confirmed:',
      totalGuests: 'Total guests:',
      viewGuests: 'View guests →',
      progressOverview: 'Progress Overview',
      tasksCompleted: 'Tasks completed',
      budgetSpent: 'Budget spent',
      guestsConfirmed: 'Guests confirmed',
      tasksCompletedValue: '{{done}} / {{total}} tasks completed',
      guestsConfirmedValue: '{{confirmed}} / {{total}} guests confirmed',
    },

    // ===== CHECKLIST =====
    checklist: {
      title: 'CHECKLIST',
      infoContent: "Your all-in-one checklist — pre-filled with smart tasks, useful tips, and space for your own chaos. Add new to-dos, set due dates, and watch your completion chart climb.",
      infoFeatures: 'Features:',
      info1: 'Pre-filled tasks that automatically adjust based on your wedding date',
      info2: 'Helpful tips from The UNFILTERED COLLECTIVE for each task',
      info3: 'Add your own custom tasks with relative time options (e.g., "6 months before")',
      info4: 'Mark tasks as completed and track your progress',
      info5: 'View completed tasks in a separate section',
      info6: 'Add personal notes to each task',
      infoWarning: 'Warning: It\'s oddly satisfying to check off those boxes!',
      months12Before: '12 months before',
      months10Before: '10 months before',
      months9Before: '9 months before',
      months8Before: '8 months before',
      months6Before: '6 months before',
      months5Before: '5 months before',
      months3Before: '3 months before',
      months2Before: '2 months before',
      month1Before: '1 month before',
      weeks3Before: '3 weeks before',
      weeks2Before: '2 weeks before',
      week1Before: '1 week before',
      days3Before: '3 days before',
      day1Before: '1 day before',
      completedTasks: 'Completed Tasks',
      newTask: 'New task...',
      taskName: 'Task name',
      selectTime: 'Select time before wedding',
      save: 'Save',
      noTasks: 'No tasks yet. Add your first task above!',
      noDate: 'No date',
      due: 'Due',
      notes: 'Notes',
      addNotes: 'Add notes...',
      deleteTask: 'Delete task',
      calendar: 'Calendar',
      calendarSoon: 'Calendar view coming soon',
      progress: 'Progress',
      completed: 'Completed',
      pending: 'Pending',
      status: 'Status',
      msgStart: 'Start checking off tasks! Every tick counts.',
      msg100: "Congratulations! You've completed all tasks.",
      msg75: 'Almost done! Keep going.',
      msg50: "You're almost there! Final push.",
      msg25: "You're halfway there! Don't stop.",
      msgOnTrack: "You're on the right track! Keep going.",
      confirmDelete: 'Are you sure you want to delete this task?',
      deleteTaskTitle: 'Delete task',
      deleteLabel: 'Delete',
      errorCreate: 'Error creating task. Please try again.',
      errorDelete: 'Error deleting task. Please try again.',
    },

    // ===== BUDGET MANAGER =====
    budget: {
      title: 'BUDGET MANAGER',
      infoContent: 'The not-so-fun-but-super-necessary part. Add all your expenses, organise them by category, and keep an eye on the chart — it\'ll tell you if you\'re still safe or already living dangerously.',
      infoNumbers: 'Numbers only, please. No dots or commas — keep it simple so we can do the math for you.',
      infoFeatures: 'Features:',
      info1: 'Set your total wedding budget',
      info2: 'Add expenses by category',
      info3: 'Track spending with visual progress indicators',
      info4: 'See total spent and remaining budget',
      info5: 'Differentiate between paid and pending expenses',
      msg25: "Early days. Save your energy for what's coming.",
      msg50: '{{pct}}% down, {{rest}}% to go. Time for a little gut check before you keep swiping.',
      msg75: "Heads up — you're getting close! Double-check you haven't forgotten any big expenses (rings, favours, suppliers).",
      msg90: "Careful — 90% gone means there's not much wiggle room left. Breathe, prioritise, and maybe skip the ice sculpture.",
      msg100: "Budget complete ✅ — but the wedding isn't. Time to decide: trim or top up?",
      msg110: 'Slightly over. Blame it on the flowers, or the open bar, or… well, you.',
      msg125: "Quarter over budget. At this point, your wedding owes you a gift.",
      msg150: "Budget? What budget? You're officially in freestyle mode now.",
      msg175: "You've gone from 'wedding planning' to 'wedding investing.'",
      msg200: "Double the budget, double the drama. Congrats, you're running the Olympics of weddings.",
      ofBudget: '{{pct}} of budget',
      newCategory: 'New Category',
      categoryName: 'Category name',
      add: 'Add',
      estimatedCost: 'Estimated cost',
      finalCost: 'Final cost',
      concept: 'CONCEPT',
      estimatedCostCol: 'ESTIMATED COST',
      finalCostCol: 'FINAL COST',
      paid: 'PAID',
      actions: 'Actions',
      edit: 'Edit',
      noExpenses: 'No expenses yet. Click "Add new expense" to add one.',
      addNewExpense: 'Add new expense',
      addNewExpenseTitle: 'Add New Expense',
      expenseName: 'Expense name',
      paidLabel: 'Paid',
      myBudget: 'My Budget',
      modifyBudget: 'Modify budget',
      print: 'Print',
      download: 'Download',
      howMuch: 'How much does my wedding cost?',
      addToSeeChart: 'Add expenses to see the chart',
      export: 'Export',
      resetBudget: 'Reset budget',
      highlight: 'Highlight',
      category: 'Category',
      estimated: 'Estimated',
      difference: 'Difference',
      noCategories: 'No categories yet. Create one to get started.',
      selectCategory: 'Select a category from the left to view its details',
      estimatedLabel: 'estimated',
      spentLabel: 'spent',
      underBudget: 'under budget',
      total: 'Total',
      newCategoryPlaceholder: 'New category - Add your first expense',
      confirmDeleteExpense: 'Are you sure you want to delete this expense?',
      confirmDeleteCategory: 'Are you sure you want to delete the category "{{name}}" and all its expenses?',
      confirmReset: 'Are you sure you want to reset the entire budget? This will delete all expenses.',
      deleteExpenseTitle: 'Delete expense',
      deleteCategoryTitle: 'Delete category',
      resetTitle: 'Reset budget',
      resetLabel: 'Reset',
      comingSoon: 'coming soon',
      errorUpdate: 'Error updating budget. Please try again.',
      errorCategory: 'Error creating category. Please try again.',
      errorExpense: 'Error creating expense.',
      errorExpenseUpdate: 'Error updating expense. Please try again.',
      errorExpenseDelete: 'Error deleting expense. Please try again.',
      errorCategoryDelete: 'Error deleting category. Please try again.',
      errorPermission: 'Permission denied. Please check that you have access to this wedding.',
      errorMissingField: 'Missing required field. Please check the category details.',
      errorExpenseVerify: 'Please verify that you have a wedding configured and that all fields are valid.',
      selectCategoryFirst: 'Please select a category first.',
      enterConcept: 'Please enter a concept for the expense.',
      validNumber: 'Estimated cost must be a valid number.',
      validNumberFinal: 'Final cost must be a valid number.',
      nonNegative: 'Estimated cost must be a valid number and non-negative.',
      nonNegativeFinal: 'Final cost must be a valid number and non-negative.',
      userNotFound: 'User not found. Please sign in again.',
    },

    // ===== GUEST MANAGER =====
    guestManager: {
      title: 'GUEST MANAGER',
      infoContent: 'Your Guest Hub — Every guest in one place. Keep all their info, RSVPs, dietary needs, +1s, and table assignments organized and up-to-date. No spreadsheets, no chaos — just clarity.',
      infoFeatures: 'Features:',
      infoGuestList: 'Guest list: Build your complete guest list',
      infoLifesaver: 'The Lifesaver Link: Share a link for guests to add themselves with customizable form fields',
      infoInvite: 'Send an invite: Design and send beautiful invitations',
      infoReminder: 'RSVP Reminder: Send reminders to guests who haven\'t RSVP\'d',
      infoSeats: 'Seat Allocation: Create and manage your seating chart',
      infoPlusOne: 'How the +1 System Works',
      infoPlusOneWhen: 'When a guest has a +1:',
      infoPlusOneLi1: 'Check the "Has +1" box when adding or editing a guest',
      infoPlusOneLi2: 'Fill in the +1\'s details (first name, last name, email, phone)',
      infoPlusOneLi3: 'In the list, you\'ll see the full name of the +1 in the "+1" column',
      infoPlusOneLi4: 'The +1 will appear as a separate guest in the list',
      infoPlusOneIs: 'When a guest IS a +1:',
      infoPlusOneIsLi1: 'In the "+1" column you\'ll see: "+1 (Main Guest\'s Name)"',
      infoPlusOneIsLi2: 'This helps you quickly identify who they\'re accompanying',
      infoPlusOneIsLi3: 'The +1 maintains their own information (RSVP, dietary requirements, etc.)',
      infoExample: 'Example:',
      infoExampleLi1: 'Main guest: "John Smith" → +1 Column: "Jane Smith"',
      infoExampleLi2: '+1 guest: "Jane Smith" → +1 Column: "+1 (John Smith)"',
      infoNote: 'Note: You can also add +1s from the public Lifesaver Link form. Guests can indicate if they\'re bringing a plus one when completing the form.',
      guestList: 'Guest list',
      lifesaverLink: 'The Lifesaver Link',
      sendInvite: 'Send an invite',
      gentleReminder: 'Gentle Reminder Time',
      rsvpReminder: 'RSVP Reminder',
      seatAllocation: 'SEAT ALLOCATION',
      yourGuestList: 'Your official guest list',
      import: 'Import',
      export: 'Export',
      print: 'Print',
      highlight: 'Highlight',
      collectDetails: 'Collect details',
      settings: 'Settings',
      addGuest: 'Add guest',
      all: 'All',
      regretfullyDeclined: 'Regretfully declined',
      waitingReply: 'Waiting for reply',
      notInvitedYet: 'Not invited yet',
      filterBy: 'Filter by...',
      hasPlusOne: 'Has +1',
      noPlusOne: 'No +1',
      group: 'Group',
      list: 'List',
      search: 'Search...',
      firstName: 'First name',
      lastName: 'Last name',
      dietary: 'Dietary',
      dietaryRequirements: 'Dietary requirements',
      allergies: 'Allergies',
      phone: 'Phone',
      tableNumber: 'Table Number',
      position: 'Position',
      none: 'None',
      edit: 'Edit',
      delete: 'Delete',
      actions: 'Actions',
      noGuests: 'No guests found. Click "Add guest" to get started.',
      guestSettings: 'Guest settings',
      settingsNote: 'Note: Your dashboard may take longer to load if you display too many columns at once.',
      basicDetails: 'Basic details',
      chooseColumns: 'Choose basic columns to display.',
      saveSettings: 'Save Settings',
      addGuestTitle: 'Add Guest',
      firstNameRequired: 'First name *',
      lastNameRequired: 'Last name *',
      rsvpStatus: 'RSVP Status',
      hasPlusOneLabel: 'Has +1',
      plusOneFirstName: '+1 First name',
      plusOneLastName: '+1 Last name',
      plusOneEmail: '+1 Email',
      plusOnePhone: '+1 Phone',
      guestListLabel: 'List',
      guestGroup: 'Group',
      tableNum: 'Table',
      positionLabel: 'Position',
      notesLabel: 'Notes',
      editGuestTitle: 'Edit Guest',
      nameRequired: 'First name and last name are required.',
      plusOneRequired: 'Plus one first name and last name are required when "Has +1" is checked.',
      guestAdded: 'Guest added successfully!',
      guestUpdated: 'Guest updated successfully!',
      confirmDelete: 'Are you sure you want to delete this guest?',
      deleteGuestTitle: 'Delete guest',
      errorDelete: 'Error deleting guest. Please try again.',
      errorAdd: 'Error adding guest. Please try again.',
      errorUpdate: 'Error updating guest.',
      errorNoWedding: 'Error: No wedding found. Please make sure you have created a wedding first.',
      errorDuplicate: 'Error: A guest with this email already exists.',
      errorPermission: 'Error: Permission denied. Please check your database policies.',
      columnsSaved: 'Column settings saved successfully!',
      errorSettings: 'Error saving settings. Please try again.',
      leaveWithoutSaving: 'Leave without saving? Your column settings will not be saved.',
      discardChanges: 'Discard changes?',
      leave: 'Leave',
      stay: 'Stay',
      leaveAddGuest: 'Leave without saving? The guest will not be added.',
      leaveEditGuest: 'Leave without saving? Your changes to this guest will not be saved.',
      plusOneDetails: 'Plus One Details',
      adding: 'Adding...',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      notInvited: 'Not invited yet',
      waiting: 'Waiting for reply',
      confirmed: 'Confirmed',
      importSoon: 'Import functionality coming soon',
      exportSoon: 'Export functionality coming soon',
      printSoon: 'Print functionality coming soon',
      highlightSoon: 'Highlight functionality coming soon',
      collectDetailsSoon: 'Collect details functionality coming soon',
      placeholderGroup: 'e.g., Family, Friends, Work, College',
      placeholderDietary: 'e.g., Vegan, Vegetarian',
      placeholderAllergies: 'e.g., Nut allergies, Shellfish, Dairy',
      placeholderList: 'e.g., A, B',
    },

    // ===== LIFESAVER LINK =====
    lifesaver: {
      title: 'GUEST MANAGER - THE LIFESAVER LINK',
      infoContent: 'Share a simple link with your guests so they can add themselves to your guest list. No more manual entry — guests fill in their own details, and you get everything organized automatically.',
      infoHow: 'How it works:',
      info1: 'Generate a unique link for your wedding',
      info2: 'Share via SMS, WhatsApp, or email',
      info3: 'Guests add their information directly',
      info4: 'All details appear in your Guest Manager automatically',
      info5: 'Option to collect household information (plus-ones, family groups)',
      contactCollector: 'Your Contact Collector',
      active: 'Active',
      description: 'This is your unique and permanent form. Share this link with all your guests. You can modify the fields or deactivate it whenever you want.',
      formFields: 'Form Fields',
      formFieldsHint: 'Choose what information you want to collect. You can modify this at any time.',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      required: '(required)',
      phoneNumber: 'Phone Number',
      country: 'Country',
      address: 'Address',
      plusOne: 'Plus One',
      copyLink: 'Copy link',
      copied: 'Copied!',
      preview: 'Preview',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      changesSaved: 'Changes saved successfully!',
      errorSaving: 'Error saving changes. Please try again.',
      enabled: 'Form enabled',
      collectHousehold: 'Collect household information',
      errorLoading: 'Error loading form',
      formStatus: 'Form Status',
      formActive: 'The form is active. Guests can access the link and submit their data.',
      formInactive: 'The form is inactive. Guests cannot access the link.',
      enableCollector: 'Enable Contact Collector',
      formActiveLabel: 'Form is active',
      formInactiveLabel: 'Form is inactive',
      enableCollectHousehold: 'Enable Collect Household',
      magicLink: 'Your Magic Link (Permanent)',
      linkNeverChanges: 'This link never changes',
      shareLinkDesc: 'Share this link with all your guests. It will always be the same, even if you modify the form fields.',
      copyLinkBtn: 'Copy Link',
      previewAsGuest: 'Preview as Guest',
      saveReminder: 'Remember: After modifying the fields or changing the status, click on "Save Changes" to apply the modifications.',
      collectAddressesHelp: 'Collect physical addresses for sending invitations',
    },

    // ===== SUPPLIER MANAGER =====
    supplier: {
      title: 'SUPPLIER MANAGER',
      infoContent: "Your wedding supplier hub — Because behind every epic wedding, there's a team that makes the magic happen. Compare quotes, track your bookings, and keep everything (and everyone) under control.",
      infoFeatures: 'Features:',
      info1: 'Numbers, please: Compare quotes side by side, filter by price, and mark your favourites as booked',
      info2: 'The chosen ones: Manage your contracted suppliers — arrival times, payments, and key dates',
      info3: 'Store contact information, rates, and important dates',
      info4: 'Track payments and due dates with reminders',
      infoTag: 'One dashboard, zero supplier chaos.',
      dashboard: 'Dashboard',
      numbersPlease: 'Numbers, please',
      chosenOnes: 'The chosen ones',
      numbersTitle: 'SUPPLIER MANAGER — NUMBERS, PLEASE',
      numbersInfo: 'All your maybe-someday suppliers go here. Compare, judge (a little), and choose your favourites.',
      chosenTitle: 'SUPPLIER MANAGER — THE CHOSEN ONES',
      chosenInfo: "Here's your Dream Team — the legends who actually made the cut. Keep track of their arrival and wrap-up times, crew meals, and payments (because even magic needs a schedule).",
      chosenInfoLi1: 'Manage contracted suppliers with full details',
      chosenInfoLi2: 'Track arrival and end times, meal requirements, dietaries',
      chosenInfoLi3: 'Record initial and second payments with due dates',
      chosenInfoLi4: 'Click a supplier to edit details, contacts, payments',
      vendors: 'Vendors',
      addSupplier: 'Add supplier',
      businessName: 'Business name',
      category: 'Category',
      contactName: 'Contact Name',
      web: 'Web',
      arrivalTime: 'Arrival Time',
      endTime: 'End Time',
      mealRequired: 'Meal Required',
      dietaries: 'Dietaries',
      totalCost: 'Total Cost Package',
      booked: 'Booked',
      errorUpdate: 'Error updating supplier',
      errorAdd: 'Error adding supplier',
      errorDelete: 'Error deleting supplier',
      businessRequired: 'Business name is required',
      confirmDelete: 'Delete this supplier?',
      deleteSupplierTitle: 'Delete supplier',
      unbook: 'Unbook',
      confirmUnbook: 'Unbook this supplier? They will move back to Numbers, please.',
      unbookTitle: 'Unbook',
      vendor: 'Vendor',
      businessNameCol: 'Business Name',
      inclusionsNotes: 'Inclusions/Notes',
      initialPayment: 'Initial Payment',
      dueDate: 'Due Date',
      paid: 'Paid',
      secondPayment: '2nd Payment',
      yes: 'Yes',
      no: 'No',
      noSuppliers: 'No suppliers yet. Click "Add supplier" to get started.',
      exportSoon: 'Export coming soon',
      addVendor: 'Add vendor',
      numbersInfoFeatures: 'Add multiple supplier quotes for the same service',
      numbersInfoFeatures2: 'Filter by cost: cheapest to most expensive or vice versa',
      numbersInfoFeatures3: 'Track contact info, arrival times, and inclusions',
      numbersInfoCols: 'Columns:',
      numbersInfoCol1: 'Booked — Tick the box to move that supplier to "The chosen ones" (your booked team).',
      numbersInfoCol2: 'Total Cost package — Click the column header to sort by cost (ascending or descending).',
      numbersInfoCol3: 'Actions — Edit details or delete a supplier.',
      noSuppliersAddVendor: 'No suppliers yet. Click "Add vendor" to add one.',
      markAsBooked: 'Mark as booked → moves to The chosen ones',
      leaveEditVendor: 'Leave without saving? Your changes to this vendor will not be saved.',
      save: 'Save',
      noBookedSuppliers: 'No booked suppliers yet. Go to "Numbers, please" and mark suppliers as Booked.',
      editVendor: 'Edit vendor',
      addVendorNavigate: 'Add vendor - navigate to Numbers please',
      details: 'Details',
      contacts: 'Contacts',
      payments: 'Payments',
      documents: 'Documents',
      deleteVendorConfirm: 'Delete this vendor permanently?',
      deleteVendorTitle: 'Delete vendor',
      errorSavingSupplier: 'Error saving supplier',
      errorDeletingVendor: 'Error deleting vendor',
      untitled: 'Untitled',
      name: 'Name',
      categoryVendorType: 'Category / Vendor type',
      placeholderPhotographer: 'e.g. Photographer',
      placeholderTime: 'e.g. 10:00',
      placeholderVegetarian: 'e.g. Vegetarian',
      inclusionsNotesLabel: 'Inclusions / Notes',
      totalCostPackage: 'Total Cost Package',
      paidSoFar: 'Paid so far:',
      remainingToPay: 'Remaining to pay:',
      budgetItem: 'Budget item',
      firstPayment: 'First payment',
      secondPaymentLabel: 'Second payment',
      amount: 'Amount',
      dueOn: 'Due on',
      documentsComingSoon: 'Documents section — coming soon. You can upload contracts and files here.',
      placeholderContact: 'Contact',
      placeholderPhoneEmail: 'Phone/Email',
      placeholderArrival: 'Arrival',
      placeholderEnd: 'End',
      placeholderNotes: 'Notes',
      categoryPhotographer: 'Photographer',
      categoryVideographer: 'Videographer',
      categoryCake: 'Cake',
      categoryCatering: 'Catering',
      categoryFlorist: 'Florist',
      categoryDJ: 'DJ',
      categoryPlanner: 'Planner',
      categoryVenue: 'Venue',
      categoryHairMakeup: 'Hair & Makeup',
      categoryTransport: 'Transport',
      categoryInvitations: 'Invitations',
      categoryOther: 'Other',
      categoryWeddingPlanner: 'Wedding planner',
    },

    // ===== LISTS =====
    lists: {
      title: 'Lists',
      infoContent: 'Need a shot list for your photographer? A checklist for flowers? A packing list for the big day? Create and edit your own lists so nothing gets forgotten (and no one says "we missed the confetti photo").',
      available: 'Available Lists:',
      mustCapture: 'Must-Capture Moments',
      mustCaptureDesc: 'Photo shot list for your photographer',
      soundtrack: 'The Soundtrack',
      soundtrackDesc: 'Your wedding playlist and music selections',
      bloomBoard: 'Bloom Board',
      bloomBoardDesc: 'Floral arrangements and flower preferences',
      weddingCargo: 'Wedding Cargo',
      weddingCargoDesc: 'Packing list for the big day',
      tagline: 'All in one place, stress-free.',
    },

    // ===== TIMELINE =====
    timeline: {
      title: 'TIMELINE CREATOR',
      infoContent: "Because who doesn't love a good plan? We've added sample timelines to get you started — but feel free to make it your own. Add, remove, or shuffle rows until it actually looks like your day.",
      infoFeatures: 'Features:',
      info1: 'Choose from 4 timeline templates (click the title)',
      info2: 'Add, edit, and delete activities',
      info3: 'Link activities to your booked vendors',
      info4: 'Highlight items for planning or in the final timeline',
      info5: 'Export and print — highlights included',
      chooseTemplate: 'Choose a template to start',
      yourTimeline: 'Your timeline — click to choose template',
      addActivity: 'Add activity',
      time: 'Time',
      activity: 'Activity',
      vendor: 'Vendor',
      noActivities: 'No activities yet. Click "Add activity" or choose a template above.',
      editActivity: 'Edit activity',
      addActivityItem: 'Add activity item',
      activityPlaceholder: 'e.g. Ceremony - vows and I dos',
      notesPlaceholder: 'Optional notes (shown below activity)',
      saveActivity: 'Save activity',
      saveAndAddMore: 'Save and add more',
      deleteActivity: 'Delete this activity?',
      deleteTitle: 'Delete',
      errorSaving: 'Error saving',
      errorDeleting: 'Error deleting',
      errorTemplate: 'Error loading template',
      loadTemplate: 'Load template',
      replaceTemplate: 'Replace your timeline with the template "{{name}}"?',
      replace: 'Replace',
      comingSoon: 'Coming soon — this feature will be enabled in a future update.',
      leaveWithoutSaving: 'Leave without saving? Your changes will not be saved.',
      buildYourWay: 'Build It Your Way',
      startFromScratch: 'Start from scratch — add your own events',
    },

    // ===== COMING SOON =====
    comingSoon: {
      msg0: "We're crafting the perfect shot list tool — because no one should miss the confetti moment.",
      msg1: "Good things take a minute. We're preparing this feature carefully so it's worth the wait.",
      msg2: "We're building this. Slowly. Properly. No half-baked tools here — just quality in the making.",
      msg3: "Not live yet — but it's on our radar (and our to-do list). We're packing this feature carefully.",
      msg4: "Coming soon-ish. We're making it worth the wait — good things come to those who plan.",
    },

    // ===== COMING SOON / LOADER =====
    common: {
      loading: 'Loading...',
      comingSoon: 'Coming Soon',
    },
  },

  es: {
    // ===== NAV =====
    nav: {
      home: 'Inicio',
      aboutUs: 'Nosotros',
      contact: 'Contacto',
      login: 'Iniciar sesión',
      register: 'Registrarse',
      dashboard: 'Panel',
      profile: 'Perfil',
      signOut: 'Cerrar sesión',
      toggleMenu: 'Abrir menú',
      closeMenu: 'Cerrar menú',
      userMenu: 'Menú de usuario',
      langEn: 'EN',
      langEs: 'ES',
      translateToSpanish: 'Traducir al español',
      translateToEnglish: 'Traducir al inglés',
    },

    // ===== DIALOG =====
    dialog: {
      information: 'Información',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
      ok: 'Aceptar',
    },

    // ===== HOME =====
    home: {
      heroTitle: 'La planificación de tu boda, sin filtros.',
      heroDescription: 'Si estás sobreviviendo entre Excels, grupos de WhatsApp y un presupuesto que ya no entiendes — este es tu centro de mando.',
      heroSlogan: '(todas nuestras herramientas son GRATIS mientras lanzamos)',
      sectionTitle: 'Por fin, una herramienta de bodas que apaga fuegos de verdad.',
      sectionSubtitle: 'Creada por profesionales reales, para que el caos no te queme y tú te quedes con lo bueno.',
      paragraph1: '¿Emails, cafés, maratones de Netflix? Tu boda no debería prenderlo todo.',
      paragraph2: 'Estamos creando herramientas para planificar tu boda de forma simple (y sin perder la cabeza). Nada de documentos eternos ni carpetas imposibles — solo claridad y flow.',
      paragraph3: 'Regístrate hoy y accede gratis a todas las herramientas a medida que vayan saliendo — por ahora sin límites. Sin planes, sin presión. Solo acceso anticipado.',
      paragraph4: 'Lo sabemos — las bodas estresan (a ti, a tu pareja y, seamos honestos… hasta a la tía intensa). Por eso hay profesionales de bodas detrás, apagando fuegos antes de que empiecen y asegurándose de que nada se escape.',
      paragraph5: 'The Unfiltered Collective.',
      paragraph5b: 'Nosotros apagamos los fuegos — tú dedícate a lo importante (como disfrutar de estar prometidos).',
      ctaButton: 'Recupera el control',
    },

    // ===== ABOUT US =====
    about: {
      title: 'POR QUÉ TE ENCANTARÁ',
      titleLine2: 'TENERNOS EN TU EQUIPO',
      feature01Title: 'Un checklist basado en bodas reales.',
      feature01Text: 'Detrás de esta plataforma hay un equipo que ha vivido cientos de bodas desde dentro. Sabemos qué funciona, qué se suele olvidar y esos pequeños detalles que lo cambian todo. Esa experiencia está integrada en tu espacio de planificación — para que cada checklist, recordatorio y consejo se sienta menos como una plantilla cualquiera y más como tener a alguien con experiencia, tranquilo y en control, a tu lado.',
      feature02Title: 'Controla tu presupuesto sin volverte loco.',
      feature02Text: 'El dinero habla… y en una boda, grita. Con vistas claras y pequeños recordatorios, siempre sabrás qué está cubierto y qué viene después. Es como tener a alguien de tu lado manteniendo los números bajo control — para que tú puedas centrarte en disfrutar.',
      feature03Title: 'Lista de invitados, bajo control',
      feature03Text: 'RSVPs, acompañantes, preferencias de comida… todo tiene su lugar aquí y se actualiza automáticamente. En vez de notas dispersas y chats familiares interminables, verás todo claramente en un solo sitio. Es como tener un asistente personal que se encarga de todo, sin que tengas que mover un dedo.',
      feature04Title: 'Proveedores al alcance de tu mano',
      feature04Text: 'Desde contratos hasta números de teléfono, todo sobre tus proveedores está junto donde realmente puedes encontrarlo. Sin buscar en correos viejos cuando necesites confirmar entregas — solo la tranquila seguridad de saber que todo está ahí.',
      feature05Title: 'Todo en un solo lugar',
      feature05Text: 'A medida que avances en tu planificación, verás cómo todo se une en un solo hub, alineado y organizado. Es ese tipo de claridad que te hace exhalar y darte cuenta de que sí, esto está bajo control.',
      feature06Title: 'Celebra sin el caos',
      feature06Text: 'Cuando llegue tu gran día, tendrás:',
      feature06Li1: 'Toda la información de tu boda en un hub organizado, tranquilo y fácil de usar.',
      feature06Li2: 'Sin búsquedas frenéticas en grupos de chat.',
      feature06Li3: 'Más tiempo para el champán, menos para hojas de cálculo.',
      promiseTitle: 'LA PROMESA UNFILTERED:',
      promiseText: 'Abrazar lo que ocurre en medio.',
    },

    // ===== CONTACT =====
    contact: {
      title: 'CONTÁCTANOS',
      description: '¿Tienes una pregunta o quieres ponerte en contacto? Completa el formulario y te responderemos lo antes posible.',
      firstName: 'Nombre',
      lastName: 'Apellido',
      phoneNumber: 'Teléfono',
      email: 'Correo electrónico',
      inquiryLabel: '¿Sobre qué te gustaría consultarnos?',
      placeholderName: 'Nombre',
      placeholderLastname: 'Apellido',
      placeholderPhone: '+34 612 345 678',
      placeholderEmail: 'tu@email.com',
      placeholderMessage: 'Cuéntanos tu consulta...',
      sendMessage: 'Enviar mensaje',
      sending: 'Enviando...',
      successMessage: '¡Gracias {{name}}! Hemos recibido tu mensaje. Te responderemos lo antes posible.',
      errorSubmit: 'Hubo un error al enviar el formulario. Por favor, inténtalo de nuevo.',
      errorNetwork: 'Error de conexión. Por favor, inténtalo de nuevo en un momento.',
    },

    // ===== AUTH =====
    auth: {
      loginWelcome: '¡Bienvenido de nuevo! Inicia sesión para continuar planificando tu boda.',
      registerWelcome: 'Empieza aquí.\nRespira, completa tus datos y crea tu espacio — simple, claro, bajo control.',
      emailLabel: 'Correo electrónico',
      passwordLabel: 'Contraseña',
      forgotPassword: '¿Olvidaste tu contraseña?',
      showPassword: 'Mostrar',
      hidePassword: 'Ocultar',
      signIn: 'Vamos',
      signingIn: 'Iniciando sesión...',
      signUp: 'Registrarse',
      signInLink: 'Iniciar sesión',
      noAccount: '¿No tienes cuenta?',
      hasAccount: '¿Ya tienes cuenta?',
      nameLabel: 'Nombre',
      lastNameLabel: 'Apellido',
      partnerName: 'Nombre de tu pareja',
      partnerLastName: 'Apellido de tu pareja',
      partnerEmail: 'Correo de tu pareja',
      weddingDate: 'Fecha de tu boda',
      budget: 'Presupuesto',
      letsDoThis: 'Hecho',
      registering: 'Registrando...',
      errorSignIn: 'Error al iniciar sesión. Por favor, inténtalo de nuevo.',
      errorRegister: 'Error al registrarse. Por favor, inténtalo de nuevo.',
      fillRequired: 'Por favor completa todos los campos requeridos',
      passwordMin: 'La contraseña debe tener al menos 6 caracteres',
      emailExists: 'Este correo ya está registrado. Inicia sesión o usa otro correo.',
      partnerEmailMustBeDifferent: 'El correo de tu pareja debe ser diferente al tuyo. Por favor usa otro correo.',
      validEmail: 'Por favor introduce una dirección de correo válida.',
      accountCreated: 'Tu cuenta ha sido creada. Por favor inicia sesión con tu correo y contraseña para usar tu panel.',
      checkEmail: 'Por favor revisa tu correo para confirmar tu cuenta y luego inicia sesión.',
    },

    // ===== FOOTER =====
    footer: {
      instagramText: 'Visita nuestro IG para consejos prácticos de bodas y lo que nadie te cuenta sobre planificar una boda.',
      followInstagram: 'Síguenos en Instagram',
      copyright: '© 2025 The UNFILTERED COLLECTIVE. Todos los derechos reservados.',
      credit: 'Diseñado y desarrollado por',
    },

    // ===== SIDEBAR / DASHBOARD =====
    sidebar: {
      toggle: 'Abrir menú lateral',
      close: 'Cerrar menú',
      dashboard: 'Panel',
      checklist: 'Lista de tareas',
      budgetManager: 'Presupuesto',
      timelineCreator: 'Cronograma',
      guestManager: 'INVITADOS',
      guestList: 'Lista de invitados',
      lifesaverLink: 'Enlace salvavidas',
      craftInvite: 'Crea tu invitación',
      gentleReminder: 'Recordatorios',
      whoSitsWhere: 'Quién se sienta dónde',
      supplierManager: 'PROVEEDORES',
      numbersPlease: 'Números por favor',
      chosenOnes: 'Los elegidos',
      lists: 'LISTAS',
      mustCapture: 'Momentos a capturar',
      soundtrack: 'Banda sonora',
      bloomBoard: 'Tablero floral',
      weddingCargo: 'Equipaje de boda',
    },

    // ===== PRICING =====
    pricing: {
      title: 'PRECIOS',
      findFit: 'Encuentra tu plan',
      freeNow: 'Por ahora, todas las herramientas son gratis.',
      freeText1: 'Empieza a planificar tu boda hoy con nuestro paquete completo de herramientas gratuitas.',
      freeText2: 'Sin tarjeta de crédito, sin cargos ocultos. Solo regístrate y comienza a planificar.',
      smoothText: 'Planificación de bodas fluida (sin la etiqueta de precio aterradora).',
      description1: 'Esto es lo que obtienes con cada plan — claro, simple y pensado para mantener tu planificación en movimiento sin el caos.',
      description2: 'Todas las herramientas son actualmente gratis y totalmente funcionales.',
      description3: 'Cada paquete incluye lo esencial. ¿La diferencia? Cuánto apoyo y estructura quieres en el camino.',
      chillMode: 'MODO TRANQUILO',
      chillDesc: '(Es gratis... y todas las herramientas están activas ahora.)',
      levelUp: 'SUBIR NIVEL',
      levelUpDesc: 'Más control, menos caos.',
      weddingCeo: 'CEO DE BODA',
      ceoDesc: 'Control total. Calma total.',
      backToPricing: 'Volver a precios',
      live: 'ACTIVO',
      // Chill Mode features
      checklistTitle: 'Lista de tareas dinámica',
      checklistDesc: 'Una lista preasignada y totalmente editable que se ajusta automáticamente a la fecha de tu boda.',
      checklistTag1: 'Cálculos, sin matemáticas de pánico.',
      checklistTag2: 'Tranquilidad, integrada.',
      rsvpTitle: 'RSVP',
      rsvpDesc: 'Saber quién viene (y quién no) nunca ha sido tan fácil. Los invitados confirman Sí/No, añaden acompañante, y puedes personalizar tu invitación con cualquier foto. Todo en un solo lugar y asignación de mesas fluida en el Gestor de invitados.',
      rsvpTag: 'Sin hojas de cálculo desordenadas. Sin grupos de chat interminables. Solo RSVPs que tienen sentido.',
      guestManagerTitle: 'Gestor de invitados',
      guestManagerDesc: 'Mantén a cada invitado en una lista simple — con etiquetas como familia, amigos, trabajo, para saber al instante quién es quién. Un mini panel muestra X confirmados / Y total invitados.',
      guestManagerTag: 'Claro, organizado y mucho más fácil que hacer malabares con cinco listas — además, puedes asignar invitados a mesas manualmente.',
      budgetTrackerTitle: 'Seguimiento de presupuesto',
      budgetTrackerDesc: 'Establece tu presupuesto de boda al registrarte. Luego solo añade gastos según avances — el rastreador te muestra exactamente qué % de tu presupuesto has usado.',
      budgetTrackerTag: 'Sin pánico financiero. Solo claridad.',
      // Level Up features
      rsvpLevelDesc: 'Obtén aún más control. Personaliza tu formulario RSVP para recopilar restricciones dietéticas, etiquetar invitados por grupo — familia, amigos o "necesita alojamiento" — y enviar preguntas personalizadas a cada lista.',
      rsvpLevelDesc2: 'Con el enlace salvavidas, los datos de invitados se recopilan más rápido, más fluido y con mucho menos estrés. Gestión inteligente de RSVP, bien hecha.',
      suppliersTitle: 'Gestor de proveedores',
      suppliersDesc: 'Mantén a todos tus profesionales de bodas en un solo lugar — fotógrafo, florista, catering, lo que sea. Añade sus contactos, tarifas y fechas clave para no perder el hilo (o ese hilo de email perdido).',
      suppliersTag: 'Un panel, cero caos de proveedores.',
      timelineTitle: 'Cronograma editable',
      timelineDesc: 'Un horario listo para usar que puedes personalizar según tus planes. Ajusta los horarios, añade tus notas y mantén todo en un solo lugar — para que todos sepan qué pasa y cuándo.',
      timelineTag: 'Sin adivinar, sin caos. Solo un cronograma que funciona.',
      customListsTitle: 'Listas personalizadas',
      customListsDesc: '¿Necesitas una lista de fotos para el fotógrafo? ¿Una lista para las flores? ¿Una lista de equipaje para el gran día? Crea y edita tus propias listas para que nada se olvide (y nadie diga "se nos pasó la foto del confeti").',
      customListsTag: 'Todo en un lugar, sin estrés.',
      budgetGraphicsTitle: 'Gráficos de presupuesto',
      budgetGraphicsDesc: 'Evita que tu presupuesto de boda se descontrole. Ve tus gastos en gráficos claros y coloridos — rastrea costes, detecta excesos y asegúrate de que tu día soñado no te deje en bancarrota.',
      pdfTitle: 'Documentos PDF',
      pdfDesc: 'Convierte todos tus planes de boda — cronogramas, presupuestos, listas — en PDFs elegantes y compartibles. Todo en un lugar, al día con cada detalle, y por fin sentir que lo tienes todo controlado. Sin estrés, bonito y totalmente bajo control.',
      // Wedding CEO features
      checklistCeoDesc: 'Tu lista se vuelve más inteligente. Las tareas incluyen recordatorios que puedes sincronizar directamente con tu Google Calendar — para que nada se escape y estés dos pasos adelante.',
      rsvpCeoTitle: 'RSVP y Gestor de invitados',
      rsvpCeoDesc: 'Llévalo al siguiente nivel. Ahora puedes gestionar respuestas en múltiples eventos — cena de ensayo, ceremonia, after-party — todo desde el mismo panel. Además, crea y personaliza tu plano de mesas sin esfuerzo.',
      invitesTitle: 'Diseños de invitaciones',
      invitesDesc: 'Tus invitaciones, tus reglas. Elige un diseño, ajusta colores, fuentes y texto hasta que sea 100% tú. No más plantillas aburridas — hazlas tan extra (o tranquilas) como quieras.',
      masterSpreadsheet: 'Hoja maestra',
      masterSpreadsheetDesc: 'Todo el caos de tu boda en un solo lugar. Combina el cronograma con horarios de proveedores, asigna tareas por proveedor, rastrea la música para cada momento y sabe exactamente quién se encarga de qué.',
      masterSpreadsheetTag: 'Por fin, un plan que tiene sentido.',
      everythingTalks: 'Todo se comunica',
      everythingTalksDesc: 'Nada vive aislado. Los proveedores se sincronizan con tu presupuesto, las fechas de reuniones se enlazan con tareas, y toda tu información alimenta la Hoja maestra. Es como si tu planificación de bodas por fin tuviera cerebro — y te ayuda de verdad.',
    },

    // ===== PROFILE =====
    profile: {
      freePlan: 'Plan gratuito',
      totalBudget: 'Presupuesto total:',
      editInfo: 'Editar información',
      fullName: 'Nombre completo',
      email: 'Correo electrónico',
      phoneOptional: 'Teléfono (opcional)',
      phonePlaceholder: '+34 612 345 678',
      totalWeddingBudget: 'Presupuesto total de la boda',
      budgetPlaceholder: '500000',
      saveChanges: 'Guardar cambios',
      saving: 'Guardando...',
      cancel: 'Cancelar',
      clickToChange: 'Clic para cambiar',
      dangerZone: 'Zona de peligro',
      dangerWarning: 'Una vez elimines tu cuenta, no podrás recuperarla. Se eliminará permanentemente:',
      dangerLi1: 'Tu cuenta de autenticación (correo, contraseña, autorización para iniciar sesión)',
      dangerLi2: 'Tu perfil (nombre, información personal, avatar)',
      dangerLi3: 'Todas tus bodas e información asociada',
      dangerLi4: 'Todos tus invitados y datos relacionados',
      dangerFinal: 'Esta acción es permanente e irreversible.',
      deleteAccount: 'Eliminar mi cuenta',
      confirmTitle: '¿Estás seguro?',
      confirmText: 'Esta acción no se puede deshacer. Tu cuenta y todos tus datos serán eliminados permanentemente.',
      yesDelete: 'Sí, eliminar cuenta',
      deleting: 'Eliminando...',
      profileUpdated: 'Perfil actualizado correctamente',
      errorUpdate: 'Error al actualizar el perfil. Por favor, inténtalo de nuevo.',
      invalidImage: 'Por favor selecciona un archivo de imagen válido.',
      imageTooLarge: 'La imagen es demasiado grande. Por favor selecciona una imagen menor de 10MB.',
      errorUpload: 'Error al subir la foto de perfil. Por favor, inténtalo de nuevo.',
      deleteSuccess: 'Tu cuenta ha sido eliminada correctamente.\n\n¡Lamentamos verte partir! Todos tus datos de planificación de bodas han sido eliminados de nuestro sistema.',
      deleteSuccessPartner: 'Tu perfil ha sido eliminado correctamente.\n\nTu pareja seguirá teniendo acceso a la información compartida de planificación de bodas, incluyendo invitados, tareas y todos los detalles.',
      deletePartial: 'Tu cuenta ha sido eliminada de nuestro sistema de planificación de bodas.\n\n¡Lamentamos verte partir! Si tienes algún problema, contacta con soporte.',
      deletePartialPartner: 'Tu perfil ha sido eliminado del sistema de planificación de bodas.\n\nTu pareja seguirá teniendo acceso a la información compartida de la boda.\n\nSi tienes algún problema, contacta con soporte.',
      errorDelete: 'Encontramos un problema al eliminar tu cuenta.\n\nPor favor, inténtalo más tarde o contacta con nuestro equipo de soporte.',
    },

    // ===== DASHBOARD HOME =====
    dashboardHome: {
      eventDashboard: 'PANEL DEL EVENTO',
      infoContent: 'Tu boda ya está tomando forma. Y sí, vas bien.',
      infoWhat: 'Lo que encontrarás aquí:',
      infoCountdown: 'Cuánto falta para el gran día (sin sacar la calculadora)',
      infoChecklist: 'Lo hecho, lo pendiente — todo bajo control',
      infoBudget: 'Lo que se fue... y lo que aún queda',
      infoRsvp: 'Quién viene y quién sigue dudando',
      infoNavigate: 'Usa el menú lateral para navegar por las distintas secciones y gestionar cada aspecto de tu planificación de bodas.',
      countdown: 'Cuenta atrás',
      days: 'Días',
      hours: 'Horas',
      min: 'Min',
      sec: 'Seg',
      checklist: 'Lista de tareas',
      completedTasks: 'Tareas completadas:',
      pendingTasks: 'Tareas pendientes:',
      tasksThisMonth: 'Tareas este mes:',
      viewChecklist: 'Ver lista →',
      budget: 'Presupuesto',
      spent: 'Gastado:',
      remaining: 'Restante:',
      viewBudget: 'Ver presupuesto →',
      rsvp: 'RSVP',
      confirmed: 'Confirmados:',
      totalGuests: 'Invitados totales:',
      viewGuests: 'Ver invitados →',
      progressOverview: 'Resumen de progreso',
      tasksCompleted: 'Tareas completadas',
      budgetSpent: 'Presupuesto gastado',
      guestsConfirmed: 'Invitados confirmados',
      tasksCompletedValue: '{{done}} / {{total}} tareas completadas',
      guestsConfirmedValue: '{{confirmed}} / {{total}} invitados confirmados',
    },

    // ===== CHECKLIST =====
    checklist: {
      title: 'LISTA DE TAREAS',
      infoContent: 'Tu lista completa — pre-rellenada con tareas inteligentes, consejos útiles y espacio para tu propio caos. Añade nuevas tareas, establece fechas límite y observa cómo sube tu progreso.',
      infoFeatures: 'Características:',
      info1: 'Tareas pre-rellenadas que se ajustan automáticamente según la fecha de tu boda',
      info2: 'Consejos útiles de The UNFILTERED COLLECTIVE para cada tarea',
      info3: 'Añade tus propias tareas personalizadas con opciones de tiempo relativo (ej: "6 meses antes")',
      info4: 'Marca tareas como completadas y rastrea tu progreso',
      info5: 'Ve las tareas completadas en una sección separada',
      info6: 'Añade notas personales a cada tarea',
      infoWarning: 'Aviso: Marcar casillas se vuelve peligrosamente satisfactorio',
      months12Before: '12 meses antes',
      months10Before: '10 meses antes',
      months9Before: '9 meses antes',
      months8Before: '8 meses antes',
      months6Before: '6 meses antes',
      months5Before: '5 meses antes',
      months3Before: '3 meses antes',
      months2Before: '2 meses antes',
      month1Before: '1 mes antes',
      weeks3Before: '3 semanas antes',
      weeks2Before: '2 semanas antes',
      week1Before: '1 semana antes',
      days3Before: '3 días antes',
      day1Before: '1 día antes',
      completedTasks: 'Tareas completadas',
      newTask: 'Nueva tarea...',
      taskName: 'Nombre de la tarea',
      selectTime: 'Seleccionar tiempo antes de la boda',
      save: 'Guardar',
      noTasks: 'Aún no hay tareas. ¡Añade tu primera tarea arriba!',
      noDate: 'Sin fecha',
      due: 'Fecha límite',
      notes: 'Notas',
      addNotes: 'Añadir notas...',
      deleteTask: 'Eliminar tarea',
      calendar: 'Calendario',
      calendarSoon: 'Vista de calendario próximamente',
      progress: 'Progreso',
      completed: 'Completadas',
      pending: 'Pendientes',
      status: 'Estado',
      msgStart: '¡Empieza a marcar tareas! Cada marca cuenta.',
      msg100: '¡Enhorabuena! Has completado todas las tareas.',
      msg75: '¡Casi terminado! Sigue así.',
      msg50: '¡Ya casi! El último empujón.',
      msg25: '¡Vas por la mitad! No pares.',
      msgOnTrack: '¡Vas por buen camino! Sigue así.',
      confirmDelete: '¿Estás seguro de que quieres eliminar esta tarea?',
      deleteTaskTitle: 'Eliminar tarea',
      deleteLabel: 'Eliminar',
      errorCreate: 'Error al crear la tarea. Por favor, inténtalo de nuevo.',
      errorDelete: 'Error al eliminar la tarea. Por favor, inténtalo de nuevo.',
    },

    // ===== BUDGET MANAGER =====
    budget: {
      title: 'GESTOR DE PRESUPUESTO',
      infoContent: 'La parte no tan divertida pero super necesaria. Añade todos tus gastos, organízalos por categoría y vigila el gráfico — te dirá si sigues a salvo o si ya vives peligrosamente.',
      infoNumbers: 'Solo números, por favor. Sin puntos ni comas — manténlo simple para que podamos hacer los cálculos por ti.',
      infoFeatures: 'Características:',
      info1: 'Establece tu presupuesto total de boda',
      info2: 'Añade gastos por categoría',
      info3: 'Rastrea gastos con indicadores visuales de progreso',
      info4: 'Ve el total gastado y el presupuesto restante',
      info5: 'Diferencia entre gastos pagados y pendientes',
      msg25: 'Días iniciales. Guarda energía para lo que viene.',
      msg50: '{{pct}}% hecho, {{rest}}% por hacer. Hora de una pequeña revisión antes de seguir gastando.',
      msg75: 'Ojo — ¡te estás acercando! Verifica que no hayas olvidado gastos grandes (anillos, detalles, proveedores).',
      msg90: 'Cuidado — 90% gastado significa que no queda mucho margen. Respira, prioriza y quizás salta la escultura de hielo.',
      msg100: 'Presupuesto completo ✅ — pero la boda no. Hora de decidir: recortar o añadir más.',
      msg110: 'Ligeramente pasado. Échale la culpa a las flores, o la barra libre, o… bueno, a ti.',
      msg125: 'Un cuarto sobre el presupuesto. A estas alturas tu boda te debe un regalo.',
      msg150: '¿Presupuesto? ¿Qué presupuesto? Ahora estás oficialmente en modo libre.',
      msg175: "Has pasado de 'planificar boda' a 'invertir en boda'.",
      msg200: 'Doble presupuesto, doble drama. Enhorabuena, estás dirigiendo las Olimpiadas de las bodas.',
      ofBudget: '{{pct}} del presupuesto',
      newCategory: 'Nueva categoría',
      categoryName: 'Nombre de categoría',
      add: 'Añadir',
      estimatedCost: 'Coste estimado',
      finalCost: 'Coste final',
      concept: 'CONCEPTO',
      estimatedCostCol: 'COSTE ESTIMADO',
      finalCostCol: 'COSTE FINAL',
      paid: 'PAGADO',
      actions: 'Acciones',
      edit: 'Editar',
      noExpenses: 'Aún no hay gastos. Haz clic en "Añadir gasto" para añadir uno.',
      addNewExpense: 'Añadir gasto',
      addNewExpenseTitle: 'Añadir nuevo gasto',
      expenseName: 'Nombre del gasto',
      paidLabel: 'Pagado',
      myBudget: 'Mi presupuesto',
      modifyBudget: 'Modificar presupuesto',
      print: 'Imprimir',
      download: 'Descargar',
      howMuch: '¿Cuánto cuesta mi boda?',
      addToSeeChart: 'Añade gastos para ver el gráfico',
      export: 'Exportar',
      resetBudget: 'Reiniciar presupuesto',
      highlight: 'Destacar',
      category: 'Categoría',
      estimated: 'Estimado',
      difference: 'Diferencia',
      noCategories: 'Aún no hay categorías. Crea una para empezar.',
      selectCategory: 'Selecciona una categoría a la izquierda para ver sus detalles',
      estimatedLabel: 'estimado',
      spentLabel: 'gastado',
      underBudget: 'por debajo del presupuesto',
      total: 'Total',
      newCategoryPlaceholder: 'Nueva categoría - Añade tu primer gasto',
      confirmDeleteExpense: '¿Estás seguro de que quieres eliminar este gasto?',
      confirmDeleteCategory: '¿Estás seguro de que quieres eliminar la categoría "{{name}}" y todos sus gastos?',
      confirmReset: '¿Estás seguro de que quieres reiniciar todo el presupuesto? Esto eliminará todos los gastos.',
      deleteExpenseTitle: 'Eliminar gasto',
      deleteCategoryTitle: 'Eliminar categoría',
      resetTitle: 'Reiniciar presupuesto',
      resetLabel: 'Reiniciar',
      comingSoon: 'próximamente',
      errorUpdate: 'Error al actualizar el presupuesto. Por favor, inténtalo de nuevo.',
      errorCategory: 'Error al crear la categoría. Por favor, inténtalo de nuevo.',
      errorExpense: 'Error al crear el gasto.',
      errorExpenseUpdate: 'Error al actualizar el gasto. Por favor, inténtalo de nuevo.',
      errorExpenseDelete: 'Error al eliminar el gasto. Por favor, inténtalo de nuevo.',
      errorCategoryDelete: 'Error al eliminar la categoría. Por favor, inténtalo de nuevo.',
      errorPermission: 'Permiso denegado. Comprueba que tienes acceso a esta boda.',
      errorMissingField: 'Falta un campo obligatorio. Revisa los detalles de la categoría.',
      errorExpenseVerify: 'Verifica que tengas una boda configurada y que todos los campos sean válidos.',
      selectCategoryFirst: 'Por favor selecciona primero una categoría.',
      enterConcept: 'Por favor introduce un concepto para el gasto.',
      validNumber: 'El coste estimado debe ser un número válido.',
      validNumberFinal: 'El coste final debe ser un número válido.',
      nonNegative: 'El coste estimado debe ser un número válido y no negativo.',
      nonNegativeFinal: 'El coste final debe ser un número válido y no negativo.',
      userNotFound: 'Usuario no encontrado. Por favor inicia sesión de nuevo.',
    },

    // ===== GUEST MANAGER =====
    guestManager: {
      title: 'GESTIÓN DE INVITADOS',
      infoContent: 'Tu centro de invitados — Todos en un solo lugar. Mantén su información, RSVPs, restricciones dietéticas, acompañantes (+1) y asignaciones de mesa organizadas y al día. Sin hojas de cálculo, sin caos — solo claridad.',
      infoFeatures: 'Características:',
      infoGuestList: 'Lista de invitados: Crea tu lista completa',
      infoLifesaver: 'El Enlace Salvavidas: Comparte un enlace para que los invitados se añadan con campos personalizables',
      infoInvite: 'Enviar invitación: Diseña y envía invitaciones bonitas',
      infoReminder: 'Recordatorio RSVP: Envía recordatorios a quienes no han confirmado',
      infoSeats: 'Asignación de asientos: Crea y gestiona tu plano de mesas',
      infoPlusOne: 'Cómo funciona el sistema +1',
      infoPlusOneWhen: 'Cuando un invitado tiene +1:',
      infoPlusOneLi1: 'Marca la casilla "Tiene +1" al añadir o editar un invitado',
      infoPlusOneLi2: 'Rellena los datos del acompañante (nombre, apellido, email, teléfono)',
      infoPlusOneLi3: 'En la lista verás el nombre completo del acompañante en la columna "+1"',
      infoPlusOneLi4: 'El acompañante aparecerá como invitado independiente en la lista',
      infoPlusOneIs: 'Cuando un invitado ES un +1:',
      infoPlusOneIsLi1: 'En la columna "+1" verás: "+1 (Nombre del invitado principal)"',
      infoPlusOneIsLi2: 'Esto te ayuda a identificar rápidamente a quién acompaña',
      infoPlusOneIsLi3: 'El +1 mantiene su propia información (RSVP, restricciones dietéticas, etc.)',
      infoExample: 'Ejemplo:',
      infoExampleLi1: 'Invitado principal: "Juan García" → Columna +1: "María García"',
      infoExampleLi2: 'Invitado +1: "María García" → Columna +1: "+1 (Juan García)"',
      infoNote: 'Nota: También puedes añadir +1s desde el formulario público del Enlace Salvavidas. Los invitados pueden indicar si traen acompañante al completar el formulario.',
      guestList: 'Lista de invitados',
      lifesaverLink: 'El Enlace Salvavidas',
      sendInvite: 'Enviar invitación',
      gentleReminder: 'Recordatorios',
      rsvpReminder: 'Recordatorio RSVP',
      seatAllocation: 'ASIGNACIÓN DE ASIENTOS',
      yourGuestList: 'Tu lista oficial de invitados',
      import: 'Importar',
      export: 'Exportar',
      print: 'Imprimir',
      highlight: 'Destacar',
      collectDetails: 'Recoger detalles',
      settings: 'Ajustes',
      addGuest: 'Añadir invitado',
      all: 'Todos',
      regretfullyDeclined: 'Lamentablemente declinó',
      waitingReply: 'Esperando respuesta',
      notInvitedYet: 'Aún no invitado',
      filterBy: 'Filtrar por...',
      hasPlusOne: 'Tiene +1',
      noPlusOne: 'Sin +1',
      group: 'Grupo',
      list: 'Lista',
      search: 'Buscar...',
      firstName: 'Nombre',
      lastName: 'Apellido',
      dietary: 'Dieta',
      dietaryRequirements: 'Restricciones dietéticas',
      allergies: 'Alergias',
      phone: 'Teléfono',
      tableNumber: 'Nº Mesa',
      position: 'Posición',
      none: 'Ninguno',
      edit: 'Editar',
      delete: 'Eliminar',
      actions: 'Acciones',
      noGuests: 'No hay invitados. Haz clic en "Añadir invitado" para empezar.',
      guestSettings: 'Ajustes de invitados',
      settingsNote: 'Nota: El panel puede tardar más en cargar si muestras demasiadas columnas a la vez.',
      basicDetails: 'Detalles básicos',
      chooseColumns: 'Elige las columnas básicas a mostrar.',
      saveSettings: 'Guardar ajustes',
      addGuestTitle: 'Añadir invitado',
      firstNameRequired: 'Nombre *',
      lastNameRequired: 'Apellido *',
      rsvpStatus: 'Estado RSVP',
      hasPlusOneLabel: 'Tiene +1',
      plusOneFirstName: 'Nombre del +1',
      plusOneLastName: 'Apellido del +1',
      plusOneEmail: 'Email del +1',
      plusOnePhone: 'Teléfono del +1',
      guestListLabel: 'Lista',
      guestGroup: 'Grupo',
      tableNum: 'Mesa',
      positionLabel: 'Posición',
      notesLabel: 'Notas',
      editGuestTitle: 'Editar invitado',
      nameRequired: 'El nombre y apellido son obligatorios.',
      plusOneRequired: 'El nombre y apellido del acompañante son obligatorios cuando "Tiene +1" está marcado.',
      guestAdded: '¡Invitado añadido correctamente!',
      guestUpdated: '¡Invitado actualizado correctamente!',
      confirmDelete: '¿Estás seguro de que quieres eliminar este invitado?',
      deleteGuestTitle: 'Eliminar invitado',
      errorDelete: 'Error al eliminar el invitado. Por favor, inténtalo de nuevo.',
      errorAdd: 'Error al añadir el invitado. Por favor, inténtalo de nuevo.',
      errorUpdate: 'Error al actualizar el invitado.',
      errorNoWedding: 'Error: No se encontró la boda. Asegúrate de haber creado una boda primero.',
      errorDuplicate: 'Error: Ya existe un invitado con este email.',
      errorPermission: 'Error: Permiso denegado. Revisa las políticas de tu base de datos.',
      columnsSaved: '¡Ajustes de columnas guardados correctamente!',
      errorSettings: 'Error al guardar los ajustes. Por favor, inténtalo de nuevo.',
      leaveWithoutSaving: '¿Salir sin guardar? Los ajustes de columnas no se guardarán.',
      discardChanges: '¿Descartar cambios?',
      leave: 'Salir',
      stay: 'Quedarse',
      leaveAddGuest: '¿Salir sin guardar? El invitado no se añadirá.',
      leaveEditGuest: '¿Salir sin guardar? Los cambios en este invitado no se guardarán.',
      plusOneDetails: 'Detalles del acompañante',
      adding: 'Añadiendo...',
      saveChanges: 'Guardar cambios',
      saving: 'Guardando...',
      notInvited: 'Aún no invitado',
      waiting: 'Esperando respuesta',
      confirmed: 'Confirmado',
      importSoon: 'Función de importar próximamente',
      exportSoon: 'Función de exportar próximamente',
      printSoon: 'Función de imprimir próximamente',
      highlightSoon: 'Función de destacar próximamente',
      collectDetailsSoon: 'Función de recoger detalles próximamente',
      placeholderGroup: 'ej., Familia, Amigos, Trabajo, Universidad',
      placeholderDietary: 'ej., Vegano, Vegetariano',
      placeholderAllergies: 'ej., Alergia a frutos secos, Marisco, Lácteos',
      placeholderList: 'ej., A, B',
    },

    // ===== LIFESAVER LINK =====
    lifesaver: {
      title: 'GESTIÓN DE INVITADOS - EL ENLACE SALVAVIDAS',
      infoContent: 'Comparte un enlace sencillo con tus invitados para que se añadan a tu lista. Sin entradas manuales — ellos rellenan sus datos y tú lo tienes todo organizado automáticamente.',
      infoHow: 'Cómo funciona:',
      info1: 'Genera un enlace único para tu boda',
      info2: 'Comparte por SMS, WhatsApp o email',
      info3: 'Los invitados añaden su información directamente',
      info4: 'Todos los detalles aparecen en tu Gestión de Invitados automáticamente',
      info5: 'Opción de recoger información de hogar (acompañantes, grupos familiares)',
      contactCollector: 'Tu recopilador de contactos',
      active: 'Activo',
      description: 'Este es tu formulario único y permanente. Comparte este enlace con todos tus invitados. Puedes modificar los campos o desactivarlo cuando quieras.',
      formFields: 'Campos del formulario',
      formFieldsHint: 'Elige qué información quieres recoger. Puedes modificarlo en cualquier momento.',
      firstName: 'Nombre',
      lastName: 'Apellido',
      email: 'Email',
      required: '(obligatorio)',
      phoneNumber: 'Número de teléfono',
      country: 'País',
      address: 'Dirección',
      plusOne: 'Acompañante (+1)',
      copyLink: 'Copiar enlace',
      copied: '¡Copiado!',
      preview: 'Vista previa',
      saveChanges: 'Guardar cambios',
      saving: 'Guardando...',
      changesSaved: '¡Cambios guardados correctamente!',
      errorSaving: 'Error al guardar. Por favor, inténtalo de nuevo.',
      enabled: 'Formulario activado',
      collectHousehold: 'Recoger información de hogar',
      errorLoading: 'Error al cargar el formulario',
      formStatus: 'Estado del formulario',
      formActive: 'El formulario está activo. Los invitados pueden acceder al enlace y enviar sus datos.',
      formInactive: 'El formulario está inactivo. Los invitados no pueden acceder al enlace.',
      enableCollector: 'Activar recopilador de contactos',
      formActiveLabel: 'Formulario activo',
      formInactiveLabel: 'Formulario inactivo',
      enableCollectHousehold: 'Activar recoger información de hogar',
      magicLink: 'Tu enlace mágico (permanente)',
      linkNeverChanges: 'Este enlace nunca cambia',
      shareLinkDesc: 'Comparte este enlace con todos tus invitados. Siempre será el mismo, incluso si modificas los campos.',
      copyLinkBtn: 'Copiar enlace',
      previewAsGuest: 'Vista previa como invitado',
      saveReminder: 'Recuerda: Después de modificar los campos o el estado, haz clic en "Guardar cambios" para aplicar las modificaciones.',
      collectAddressesHelp: 'Recoger direcciones postales para enviar invitaciones',
    },

    // ===== SUPPLIER MANAGER =====
    supplier: {
      title: 'GESTIÓN DE PROVEEDORES',
      infoContent: 'Tu centro de proveedores — Porque detrás de cada boda épica hay un equipo que hace la magia. Compara presupuestos, haz seguimiento de reservas y mantén todo (y a todos) bajo control.',
      infoFeatures: 'Características:',
      info1: 'Números, por favor: Compara presupuestos uno al lado del otro, filtra por precio y marca tus favoritos como reservados',
      info2: 'Los elegidos: Gestiona tus proveedores contratados — horarios de llegada, pagos y fechas clave',
      info3: 'Guarda información de contacto, tarifas y fechas importantes',
      info4: 'Haz seguimiento de pagos y fechas límite con recordatorios',
      infoTag: 'Un panel, cero caos con proveedores.',
      dashboard: 'Panel',
      numbersPlease: 'Números, por favor',
      chosenOnes: 'Los elegidos',
      numbersTitle: 'GESTIÓN DE PROVEEDORES — NÚMEROS, POR FAVOR',
      numbersInfo: 'Todos tus posibles proveedores van aquí. Compara, juzga (un poco) y elige tus favoritos.',
      chosenTitle: 'GESTIÓN DE PROVEEDORES — LOS ELEGIDOS',
      chosenInfo: 'Aquí está tu Dream Team — los que pasaron el corte. Haz seguimiento de sus horarios de llegada y fin, comidas del equipo y pagos (porque hasta la magia necesita horarios).',
      chosenInfoLi1: 'Gestiona proveedores contratados con todos los detalles',
      chosenInfoLi2: 'Haz seguimiento de horarios de llegada y fin, comidas requeridas, dietas',
      chosenInfoLi3: 'Registra pagos iniciales y segundos con fechas límite',
      chosenInfoLi4: 'Haz clic en un proveedor para editar detalles, contactos, pagos',
      vendors: 'Proveedores',
      addSupplier: 'Añadir proveedor',
      businessName: 'Nombre del negocio',
      category: 'Categoría',
      contactName: 'Nombre de contacto',
      web: 'Web',
      arrivalTime: 'Hora de llegada',
      endTime: 'Hora de fin',
      mealRequired: 'Comida requerida',
      dietaries: 'Dietas',
      totalCost: 'Coste total del paquete',
      booked: 'Reservado',
      errorUpdate: 'Error al actualizar el proveedor',
      errorAdd: 'Error al añadir el proveedor',
      errorDelete: 'Error al eliminar el proveedor',
      businessRequired: 'El nombre del negocio es obligatorio',
      confirmDelete: '¿Eliminar este proveedor?',
      deleteSupplierTitle: 'Eliminar proveedor',
      unbook: 'Cancelar reserva',
      confirmUnbook: '¿Cancelar la reserva de este proveedor? Volverá a Números, por favor.',
      unbookTitle: 'Cancelar reserva',
      vendor: 'Proveedor',
      businessNameCol: 'Nombre del negocio',
      inclusionsNotes: 'Incluido/Notas',
      initialPayment: 'Pago inicial',
      dueDate: 'Fecha límite',
      paid: 'Pagado',
      secondPayment: '2º pago',
      yes: 'Sí',
      no: 'No',
      noSuppliers: 'Aún no hay proveedores. Haz clic en "Añadir proveedor" para empezar.',
      exportSoon: 'Exportar próximamente',
      addVendor: 'Añadir proveedor',
      numbersInfoFeatures: 'Añade múltiples presupuestos de proveedores para el mismo servicio',
      numbersInfoFeatures2: 'Filtra por coste: del más barato al más caro o viceversa',
      numbersInfoFeatures3: 'Haz seguimiento de contacto, horarios de llegada e inclusiones',
      numbersInfoCols: 'Columnas:',
      numbersInfoCol1: 'Reservado — Marca la casilla para mover ese proveedor a "Los elegidos" (tu equipo reservado).',
      numbersInfoCol2: 'Coste total del paquete — Haz clic en el encabezado para ordenar por coste (ascendente o descendente).',
      numbersInfoCol3: 'Acciones — Editar detalles o eliminar un proveedor.',
      noSuppliersAddVendor: 'Aún no hay proveedores. Haz clic en "Añadir proveedor" para añadir uno.',
      markAsBooked: 'Marcar como reservado → pasa a Los elegidos',
      leaveEditVendor: '¿Salir sin guardar? Los cambios en este proveedor no se guardarán.',
      save: 'Guardar',
      noBookedSuppliers: 'Aún no hay proveedores reservados. Ve a "Números, por favor" y marca proveedores como Reservados.',
      editVendor: 'Editar proveedor',
      addVendorNavigate: 'Añadir proveedor - ir a Números, por favor',
      details: 'Detalles',
      contacts: 'Contactos',
      payments: 'Pagos',
      documents: 'Documentos',
      deleteVendorConfirm: '¿Eliminar este proveedor permanentemente?',
      deleteVendorTitle: 'Eliminar proveedor',
      errorSavingSupplier: 'Error al guardar el proveedor',
      errorDeletingVendor: 'Error al eliminar el proveedor',
      untitled: 'Sin título',
      name: 'Nombre',
      categoryVendorType: 'Categoría / Tipo de proveedor',
      placeholderPhotographer: 'ej. Fotógrafo',
      placeholderTime: 'ej. 10:00',
      placeholderVegetarian: 'ej. Vegetariano',
      inclusionsNotesLabel: 'Incluido / Notas',
      totalCostPackage: 'Coste total del paquete',
      paidSoFar: 'Pagado hasta ahora:',
      remainingToPay: 'Por pagar:',
      budgetItem: 'Concepto de presupuesto',
      firstPayment: 'Primer pago',
      secondPaymentLabel: 'Segundo pago',
      amount: 'Cantidad',
      dueOn: 'Vencimiento',
      documentsComingSoon: 'Sección de documentos — próximamente. Podrás subir contratos y archivos aquí.',
      placeholderContact: 'Contacto',
      placeholderPhoneEmail: 'Teléfono/Correo electrónico',
      placeholderArrival: 'Llegada',
      placeholderEnd: 'Fin',
      placeholderNotes: 'Notas',
      categoryPhotographer: 'Fotógrafo',
      categoryVideographer: 'Videógrafo',
      categoryCake: 'Pastel',
      categoryCatering: 'Catering',
      categoryFlorist: 'Florista',
      categoryDJ: 'DJ',
      categoryPlanner: 'Organizador',
      categoryVenue: 'Lugar',
      categoryHairMakeup: 'Peluquería y maquillaje',
      categoryTransport: 'Transporte',
      categoryInvitations: 'Invitaciones',
      categoryOther: 'Otro',
      categoryWeddingPlanner: 'Organizador de bodas',
    },

    // ===== LISTS =====
    lists: {
      title: 'Listas',
      infoContent: '¿Necesitas una lista de fotos para tu fotógrafo? ¿Una lista de flores? ¿Una lista de packing para el gran día? Crea y edita tus propias listas para no olvidar nada (y que nadie diga "se nos pasó la foto del confeti").',
      available: 'Listas disponibles:',
      mustCapture: 'Momentos imprescindibles',
      mustCaptureDesc: 'Lista de fotos para tu fotógrafo',
      soundtrack: 'La banda sonora',
      soundtrackDesc: 'Tu playlist y selección musical de la boda',
      bloomBoard: 'Tablero floral',
      bloomBoardDesc: 'Arreglos florales y preferencias de flores',
      weddingCargo: 'Equipaje de boda',
      weddingCargoDesc: 'Lista de packing para el gran día',
      tagline: 'Todo en un solo lugar, sin estrés.',
    },

    // ===== TIMELINE =====
    timeline: {
      title: 'CREADOR DE CRONOLOGÍA',
      infoContent: '¿A quién no le gusta un buen plan? Hemos añadido cronologías de ejemplo para empezar — pero siéntete libre de personalizarla. Añade, quita o reorganiza filas hasta que refleje tu día.',
      infoFeatures: 'Características:',
      info1: 'Elige entre 4 plantillas (haz clic en el título)',
      info2: 'Añade, edita y elimina actividades',
      info3: 'Enlaza actividades con tus proveedores reservados',
      info4: 'Destaca ítems para planificar o en la cronología final',
      info5: 'Exporta e imprime — incluyendo destacados',
      chooseTemplate: 'Elige una plantilla para empezar',
      yourTimeline: 'Tu cronología — haz clic para elegir plantilla',
      addActivity: 'Añadir actividad',
      time: 'Hora',
      activity: 'Actividad',
      vendor: 'Proveedor',
      noActivities: 'Aún no hay actividades. Haz clic en "Añadir actividad" o elige una plantilla arriba.',
      editActivity: 'Editar actividad',
      addActivityItem: 'Añadir ítem de actividad',
      activityPlaceholder: 'ej. Ceremonia - votos y sí quiero',
      notesPlaceholder: 'Notas opcionales (mostradas debajo de la actividad)',
      saveActivity: 'Guardar actividad',
      saveAndAddMore: 'Guardar y añadir más',
      deleteActivity: '¿Eliminar esta actividad?',
      deleteTitle: 'Eliminar',
      errorSaving: 'Error al guardar',
      errorDeleting: 'Error al eliminar',
      errorTemplate: 'Error al cargar la plantilla',
      loadTemplate: 'Cargar plantilla',
      replaceTemplate: '¿Reemplazar tu cronología con la plantilla "{{name}}"?',
      replace: 'Reemplazar',
      comingSoon: 'Próximamente — esta función se habilitará en una actualización futura.',
      leaveWithoutSaving: '¿Salir sin guardar? Tus cambios no se guardarán.',
      buildYourWay: 'Construye a tu manera',
      startFromScratch: 'Empezar desde cero — añade tus propios eventos',
    },

    // ===== COMING SOON =====
    comingSoon: {
      msg0: 'Estamos creando la herramienta perfecta de lista de fotos — porque nadie debería perderse el momento del confeti.',
      msg1: 'Las cosas buenas toman tiempo. Estamos preparando esta función con cuidado para que valga la espera.',
      msg2: 'Estamos construyendo esto. Despacio. Bien. Sin herramientas a medias — solo calidad en proceso.',
      msg3: 'Aún no está en vivo — pero está en nuestro radar (y en nuestra lista). Estamos preparando esta función con cuidado.',
      msg4: 'Próximamente. Lo estamos haciendo que valga la espera — las cosas buenas llegan a quienes planifican.',
    },

    // ===== COMING SOON / LOADER =====
    common: {
      loading: 'Cargando...',
      comingSoon: 'Próximamente',
    },
  },
}

/**
 * Obtiene el valor anidado de un objeto usando una clave con punto
 * Ej: getNested(obj, 'nav.home') → obj.nav.home
 */
function getNested(obj, path) {
  const keys = path.split('.')
  let current = obj
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined
    current = current[key]
  }
  return current
}

/**
 * Interpola variables en el texto: "Hola {{name}}" con { name: 'Juan' } → "Hola Juan"
 */
function interpolate(text, params = {}) {
  if (typeof text !== 'string') return text
  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => (params[key] != null ? String(params[key]) : `{{${key}}}`))
}

/**
 * Función de traducción
 * @param {string} lang - 'en' | 'es'
 * @param {string} key - Clave con punto, ej: 'nav.home'
 * @param {object} params - Parámetros para interpolación
 */
export function t(lang, key, params = {}) {
  const value = getNested(translations[lang], key)
  if (value === undefined) {
    // Fallback a inglés si no existe la traducción
    const fallback = getNested(translations.en, key)
    return fallback != null ? interpolate(String(fallback), params) : key
  }
  return interpolate(String(value), params)
}
